/**
 * AccountMaintenancePage — System · Account Maintenance (Bill 2026-10-17: "Build it, wire it, end to end and turn off
 * by admin toggle and prepare a ruling request for the group to approve").
 *
 * One switch per maintenance action in the officer app (ROP-teams, customer full record > Products), the master
 * switch, the signature-wait policy, and the full record's older direct-to-Core dialogs. Every action ships OFF
 * (seed 426) pending the ruling by Amy Pendleton, Samantha Callier, Peter Freiberg and Jessica Beucler; turning one on
 * changes what officers can do in production, so each change asks for confirmation.
 *
 * Wire: GET /v3/config/system-settings/rows; POST /v3/admin/system-settings (same as Critical Settings).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ADMIN_AUTH_TOKEN_SENTINEL, ApiError, type SystemSettingUpsertBody } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Card, Pill } from '@/components/primitives';

interface Row { settingKey: string; settingValue: string | null; updatedAt?: string | null; updatedBy?: string | null }

interface SwitchMeta {
  key: string;
  label: string;
  blurb: string;
  /** Proven against Core Director (opening already sends every write it makes). */
  proven?: boolean;
  defaultValue: 'true' | 'false';
}

const MASTER: SwitchMeta = { key: 'maint.enabled', label: 'Account maintenance', defaultValue: 'false',
  blurb: 'Master switch. Off: no maintenance anywhere, whatever the switches below say; the server refuses to start or submit a request.' };

// Bill 2026-10-17: "we limit the app to test CIFs 666-##-#### (no risk, needs testing)". Server-enforced for everyone
// whoever is signed in; blank or missing reads as on.
const FENCE: SwitchMeta = { key: 'maint.testCustomersOnly.enabled', label: 'Test customers only', defaultValue: 'true',
  blurb: 'On: every person on the account must be a test customer (last name Test with a 666 SSN/TIN or CIF), whoever is signed in, and every Core Director call the request makes is held to test customers. Off: maintenance works on real customers. Keep on until the ruling.' };

const ACTIONS: SwitchMeta[] = [
  { key: 'maint.action.debit-card.enabled', label: 'Issue a debit card', proven: true, defaultValue: 'false',
    blurb: 'Checking accounts. Orders a card for the chosen owners (the account-opening card issue), then print and activate on the finished screen. Nothing to sign.' },
  { key: 'maint.action.order-checks.enabled', label: 'Order checks', proven: true, defaultValue: 'false',
    blurb: 'Checking accounts. The owners sign a new signature card (the bank requires one when checks are ordered); creates the Order checks task.' },
  { key: 'maint.action.pod.enabled', label: 'Change POD beneficiaries', proven: false, defaultValue: 'false',
    blurb: 'Checking and savings. The owners sign the POD designation form. Adding is proven; removing the current beneficiaries is not yet proven against Core.' },
  { key: 'maint.action.joint-add.enabled', label: 'Add a joint owner', proven: true, defaultValue: 'false',
    blurb: 'Checking and savings. Every owner signs a new signature card. Needs a Manager\'s approval (routing rule).' },
  { key: 'maint.action.joint-remove.enabled', label: 'Remove a joint owner', proven: false, defaultValue: 'false',
    blurb: 'Checking and savings. The remaining owners sign a new signature card. Needs a Manager\'s approval. Not yet proven against Core.' },
  { key: 'maint.action.od-protection.enabled', label: 'Overdraft coverage (Reg E)', proven: false, defaultValue: 'false',
    blurb: 'Checking accounts. The primary signs the A-9 form. Not yet proven against Core.' },
  { key: 'maint.action.od-limit.enabled', label: 'Overdraft limit', proven: false, defaultValue: 'false',
    blurb: 'Checking accounts. The primary signs the A-9 form; a limit of $500 or more needs a Supervisor\'s approval. Not yet proven against Core.' },
  { key: 'maint.action.statements.enabled', label: 'E-statements on / off', proven: false, defaultValue: 'false',
    blurb: 'Checking and savings. The customer is given the E-SIGN disclosure. Not yet proven against Core.' },
];

const POLICY: SwitchMeta[] = [
  { key: 'maint.waitForSignatures', label: 'Wait for every signature before changing Core', defaultValue: 'true',
    blurb: 'On (recommended): Core Director changes only after everyone has signed. Off: maintenance follows account opening\'s policy, where Core changes first and absent signers sign afterwards.' },
  { key: 'maint.action.profile.enabled', label: 'Edit CIF on the customer record', proven: true, defaultValue: 'false',
    blurb: 'The full record\'s Edit CIF (customer details and address to Core, with the account cascade and notes).' },
  { key: 'maint.legacyRcmActions.enabled', label: 'Older direct-to-Core dialogs', defaultValue: 'false',
    blurb: 'The full record\'s earlier beneficiary, joint-owner, NSF/OD and card re-issue dialogs and Change Address. They change Core with no form or signature, or don\'t work. Leave off.' },
];

const on = (v: string | null | undefined): boolean => (v ?? '').trim().toLowerCase() === 'true';

export function AccountMaintenancePage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [note, setNote] = useState<{ tone: 'info' | 'err'; text: string } | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await client.retail.config.systemSettingsRows();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) setRows(resp.data as unknown as Row[]);
        else setLoadError(resp.error ?? 'Failed to load settings.');
      } catch (e) {
        if (!cancelled) setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [client, nonce]);

  const byKey = useMemo(() => new Map((rows ?? []).map((r) => [r.settingKey, r])), [rows]);
  const value = (m: SwitchMeta): boolean => on(byKey.get(m.key)?.settingValue ?? m.defaultValue);
  const masterOn = value(MASTER);

  const flip = useCallback(async (m: SwitchMeta) => {
    const next = !value(m);
    const ok = window.confirm(`${next ? 'Turn on' : 'Turn off'} "${m.label}"?\n\nThis changes what officers can do in production.${next && m.proven === false ? '\n\nThis change has NOT been proven against Core Director yet.' : ''}${!next && m.key === FENCE.key ? '\n\nAccount maintenance will then work on REAL customers\' accounts.' : ''}`);
    if (!ok) return;
    setSaving(m.key);
    setNote(null);
    const exists = byKey.has(m.key);
    const body: SystemSettingUpsertBody = exists
      ? { settingKey: m.key, settingValue: next ? 'true' : 'false' }
      : { settingKey: m.key, settingValue: next ? 'true' : 'false', dataType: 'bool', categoryCode: 'maintenance', description: m.blurb, defaultValue: m.defaultValue, isActive: true, isSensitive: false };
    try {
      const resp = await client.retail.admin.systemSettings.upsert(ADMIN_AUTH_TOKEN_SENTINEL, body);
      if (!resp.success) setNote({ tone: 'err', text: resp.error ?? 'The change was not saved.' });
      else { setNote({ tone: 'info', text: `${m.label}: ${next ? 'on' : 'off'}. Officers see it within a minute.` }); setNonce((n) => n + 1); }
    } catch (e) {
      setNote({ tone: 'err', text: ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, byKey]);

  const SwitchRow = ({ m, dimmed }: { m: SwitchMeta; dimmed?: boolean }): JSX.Element => {
    const isOn = value(m);
    const row = byKey.get(m.key);
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: '12px 0', borderTop: '1px solid var(--border, #e5e7eb)', opacity: dimmed ? 0.6 : 1 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong>{m.label}</strong>
            <Pill tone={isOn ? 'ok' : 'muted'}>{isOn ? 'On' : 'Off'}</Pill>
            {m.proven === false && <Pill tone="warn">Not yet proven against Core</Pill>}
            {m.proven === true && <Pill tone="info">Proven (account opening)</Pill>}
          </div>
          <div style={{ fontSize: 12.5, marginTop: 4, color: 'var(--text-muted, #475569)' }}>{m.blurb}</div>
          <div style={{ fontSize: 11, marginTop: 2, color: 'var(--text-muted, #64748b)' }}>
            <code>{m.key}</code>{row?.updatedBy ? ` · last changed by ${row.updatedBy}` : row ? '' : ' · not seeded yet (seed 426)'}
          </div>
        </div>
        <button type="button" className={`btn btn--${isOn ? 'ghost' : 'primary'} btn--sm`} disabled={saving !== null} onClick={() => void flip(m)}>
          {saving === m.key ? 'Saving…' : isOn ? 'Turn off' : 'Turn on'}
        </button>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Banner tone="warn">
        Every action ships off until Amy Pendleton, Samantha Callier, Peter Freiberg and Jessica Beucler rule on how it works, what it
        requires, and when it rolls out (ruling request, 2026-10-17). Until then the actions are on for testing with "Test customers only" on.
      </Banner>
      {loadError && <Banner tone="err">{loadError}</Banner>}
      {note && <Banner tone={note.tone === 'err' ? 'err' : 'info'}>{note.text}</Banner>}
      {!rows && !loadError ? <div>Loading…</div> : (
        <>
          <Card title="Master switch"><SwitchRow m={MASTER} /><SwitchRow m={FENCE} /></Card>
          <Card title="Actions (customer full record > Products > account menu)">
            {!masterOn && <div style={{ fontSize: 12.5, marginBottom: 4 }}>The master switch is off, so none of these show to officers yet.</div>}
            {ACTIONS.map((m) => <SwitchRow key={m.key} m={m} dimmed={!masterOn} />)}
          </Card>
          <Card title="Policy and the customer record">{POLICY.map((m) => <SwitchRow key={m.key} m={m} />)}</Card>
        </>
      )}
    </div>
  );
}
